import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useReducer,
} from "react";

/**
 * Teklif sepeti.
 *
 * Bu bir alışveriş sepeti değil: ödeme yok, fiyat yok. Müşteri ilgilendiği
 * ürünleri biriktirir, sonra WhatsApp'tan tek mesajda fiyat sorar.
 *
 * Sepet tarayıcıda saklanır, sayfa yenilense de kaybolmaz.
 */

const DEPO_ANAHTARI = "akkuzey_teklif_sepeti";

const CartContext = createContext(null);

// Aynı ürünün farklı boyutu ayrı satırdır: "luxe-parlak::15 L"
const satirId = (urunId, boyut) => `${urunId}::${boyut}`;

const baslangic = { satirlar: [] };

function reducer(durum, eylem) {
  switch (eylem.tip) {
    case "yukle":
      return { satirlar: eylem.satirlar };

    case "ekle": {
      const { urun, boyut } = eylem;
      const id = satirId(urun.id, boyut);
      const mevcut = durum.satirlar.find((s) => s.id === id);

      if (mevcut) {
        return {
          satirlar: durum.satirlar.map((s) =>
            s.id === id ? { ...s, adet: s.adet + 1 } : s
          ),
        };
      }

      return {
        satirlar: [
          ...durum.satirlar,
          {
            id,
            urunId: urun.id,
            ad: urun.ad,
            marka: urun.marka,
            gorsel: urun.gorsel,
            boyut,
            adet: 1,
          },
        ],
      };
    }

    case "adetDegistir": {
      const yeni = durum.satirlar
        .map((s) =>
          s.id === eylem.id ? { ...s, adet: Math.max(0, s.adet + eylem.fark) } : s
        )
        .filter((s) => s.adet > 0);
      return { satirlar: yeni };
    }

    case "cikar":
      return { satirlar: durum.satirlar.filter((s) => s.id !== eylem.id) };

    case "temizle":
      return { satirlar: [] };

    default:
      return durum;
  }
}

// Sepet ilk açılışta doğrudan tarayıcıdaki kayıttan başlar.
// (Ayrı bir "yükle" efekti, kaydetme efekti önce çalışıp kaydı boş listeyle ezdiği için sepeti siliyordu.)
const kayittanBaslat = () => {
  try {
    const satirlar = JSON.parse(localStorage.getItem(DEPO_ANAHTARI) || "[]");
    if (Array.isArray(satirlar)) return { satirlar };
  } catch {
    /* gizli sekme veya kapalı depolama: sepet boş başlar */
  }
  return baslangic;
};

export const CartProvider = ({ children }) => {
  const [durum, dispatch] = useReducer(reducer, baslangic, kayittanBaslat);

  // Değiştikçe kaydet
  useEffect(() => {
    try {
      localStorage.setItem(DEPO_ANAHTARI, JSON.stringify(durum.satirlar));
    } catch {
      /* kaydedilemezse sepet yine de bu oturumda çalışır */
    }
  }, [durum.satirlar]);

  const deger = useMemo(() => {
    const toplamAdet = durum.satirlar.reduce((t, s) => t + s.adet, 0);

    return {
      satirlar: durum.satirlar,
      toplamAdet,
      bos: durum.satirlar.length === 0,
      ekle: (urun, boyut) => dispatch({ tip: "ekle", urun, boyut }),
      adetDegistir: (id, fark) => dispatch({ tip: "adetDegistir", id, fark }),
      cikar: (id) => dispatch({ tip: "cikar", id }),
      temizle: () => dispatch({ tip: "temizle" }),
      icinde: (urunId, boyut) =>
        durum.satirlar.some((s) => s.id === satirId(urunId, boyut)),
    };
  }, [durum.satirlar]);

  return <CartContext.Provider value={deger}>{children}</CartContext.Provider>;
};

export const useCart = () => {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error("useCart, CartProvider içinde kullanılmalı");
  return ctx;
};
